import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { logInfo } from '../shared/utils/logging';
import { TransparentLoader } from '../components/elements/TransparentLoader';
import UpsellHeader from './UpsellHeader';
import UpsellOffers from './UpsellOffers';
import Iframe from './Iframe';
import { ReactComponent as Arrow } from '../icons/arrow.svg';

import {
  ContinueToButton,
  Modal,
  ModalContainer,
  ScrollableView,
} from './elements';
import {
  AddButtonAction,
  ButtonAction,
  isCandyRackSettings,
  Offer,
  Product,
  Settings,
} from '../types';
import { useProductsContext } from '../contexts/ProductsContext';
import { getIframeDocument } from '../utils/getIframe';
import { APPError } from '../shared/api/types';
import ErrorMessage from './elements/ErrorMessage';
import { getCurrency } from '../shared/product-page-placement/currency';
import { useSharedContext } from '../contexts/SharedContext';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { PopupContextProvider } from '../contexts/Settings';
import { RejectButton } from './elements/Buttons';
import { trackViewForCandyRackExternalAddToCart } from 'src/utils/tracking-utils-candyrack';

interface Props {
  handleAddToCart: (
    offer: Offer,
    product?: Product,
    action?: AddButtonAction,
  ) => Promise<boolean>;
  handleRemoveFromCart: (offer: Offer) => Promise<void>;
  handleClose: (
    ignoreUpgrade?: boolean,
    closeAction?: ButtonAction,
  ) => Promise<void>;
  handleContinue: () => void;
  offers: Offer[];
  settings: Settings;
  modalOpen: boolean;
  currentApp: REGISTERED_APPS;
  handleReject?: () => void;
  isLoading?: boolean;
  onError?(): void;
  error?: APPError;
  parentProduct?: Product;
  rejectInProgress?: boolean;
}

// @ts-ignore
const ListHeader = ({ text, hasSubheading }) => {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headingHeight =
      headerRef?.current?.getBoundingClientRect().height || 66;

    getIframeDocument()?.documentElement.style.setProperty(
      '--header-height',
      `${headingHeight}px`,
    );
  });

  return (
    <div ref={headerRef} className={cn('z-20 py-20 px-45')}>
      <h1 className="max-w-md m-auto text-lg text-center Title lg:text-xl text-grey-1">
        {text.title}
      </h1>
      {hasSubheading && (
        <h2 className="max-w-md m-auto text-center Subtitle text-lg-s lg:text-lg text-grey-2">
          {text.subtitle}
        </h2>
      )}
    </div>
  );
};

// @ts-ignore
const DetailHeader = ({ resetSelectedProduct, back }) => {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headingHeight = headerRef?.current?.getBoundingClientRect().height;
    getIframeDocument()?.documentElement.style.setProperty(
      '--header-height',
      `${headingHeight}px`,
    );
  });

  return (
    <div ref={headerRef} className={cn('z-20 py-15 px-20 md:pr-45 md:pl-30')}>
      <button
        className="flex items-center my-8 transition-colors duration-100 border-0 Button-Back text-grey-1 bg-none text-md hover:text-grey-2"
        data-cy="close-product-detail"
        onClick={() => resetSelectedProduct()}
      >
        <Arrow className="h-20 transform rotate-180" />
        {back}
      </button>
    </div>
  );
};

const Footer = ({
  // @ts-ignore-start
  selectedProduct,
  // @ts-ignore-start
  hasRejectButton,
  // @ts-ignore-start
  handleReject,
  // @ts-ignore-start
  isLoading,
  // @ts-ignore-start
  rejectInProgress,
  // @ts-ignore-start
  handleContinue,
  // @ts-ignore-start
  text,
}) => {
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const footerHeight = footerRef?.current?.getBoundingClientRect().height;
    getIframeDocument()?.documentElement.style.setProperty(
      '--footer-height',
      `${footerHeight}px`,
      'important',
    );
  });

  return (
    <footer
      ref={footerRef}
      className={cn(
        !selectedProduct
          ? 'Footer py-15 pr-15 md:pr-25 pl-15 border-t flex flex-col-reverse md:flex-row md:justify-end'
          : 'Overlay h-40 md:h-50 absolute md:right-0 w-full bottom-0 bg-gradient-to-t from-white via-white to-transparent pointer-events-none',
      )}
    >
      {hasRejectButton && (
        <RejectButton
          data-cy="reject-button"
          onClick={handleReject}
          disabled={isLoading || rejectInProgress}
          className={cn(
            'Button-Reject',
            selectedProduct && 'hidden',
            'mr-10 mt-10 md:mt-none',
          )}
        >
          {text.reject}
        </RejectButton>
      )}

      <ContinueToButton
        data-cy="continue-button"
        onClick={handleContinue}
        disabled={isLoading || rejectInProgress}
        className={cn('Button-Primary', selectedProduct && 'hidden')}
      >
        {text.continue_to}
      </ContinueToButton>
    </footer>
  );
};

const Popup = ({
  handleAddToCart,
  handleRemoveFromCart,
  handleClose,
  handleContinue,
  offers,
  settings,
  modalOpen,
  parentProduct,
  isLoading,
  error,
  onError,
  currentApp,
  handleReject,
  rejectInProgress,
}: Props) => {
  const { selectedProduct, resetSelectedProduct, productDetail } =
    useProductsContext();
  const { currency } = getCurrency();
  const { runningAppName } = useSharedContext();

  useEffect(() => {
    if (runningAppName) {
      logInfo('UI loaded 👩🏼‍🎨', runningAppName);
    }
  }, [runningAppName]);

  useEffect(() => {
    if (runningAppName === REGISTERED_APPS.ADD_TO_CART && offers?.length > 0) {
      void trackViewForCandyRackExternalAddToCart(offers);
    }
  }, [offers, runningAppName]);

  if (!offers) return null;

  const handleCloseDetail = () => {
    resetSelectedProduct();
    void handleClose();
  };

  const cssModalOfferId = selectedProduct
    ? `OfferId-${selectedProduct?.offerID}`
    : `OfferId-${offers[0]?.id}`;
  const cssModalTargetProductId =
    parentProduct && `Target-Product-Id-${parentProduct.id}`;

  return (
    <PopupContextProvider
      value={{
        settings,
        blockingActionInProgress: rejectInProgress,
      }}
    >
      <Iframe visible={modalOpen}>
        {isLoading && <TransparentLoader />}
        <ModalContainer modalOpen={modalOpen}>
          <Modal
            id="modal-container"
            className={cn(
              selectedProduct && 'Product-Detail-Modal',
              cssModalOfferId,
              cssModalTargetProductId,
              'Main-Window',
            )}
          >
            <ScrollableView
              header={
                <ListHeader
                  text={settings.text}
                  hasSubheading={settings.is_subheadline_enabled}
                />
              }
              footer={
                <Footer
                  hasRejectButton={
                    isCandyRackSettings(settings) && settings.is_reject_enabled
                  }
                  text={settings.text}
                  handleReject={handleReject}
                  handleContinue={handleContinue}
                  rejectInProgress={rejectInProgress}
                  isLoading={isLoading}
                  selectedProduct={selectedProduct}
                />
              }
              onClose={handleClose}
              className={cn(selectedProduct && 'hidden')}
              childClassName="Main-Content"
            >
              <>
                <UpsellHeader
                  hidden={!!selectedProduct}
                  currentApp={currentApp}
                />
                <UpsellOffers
                  offers={offers}
                  currency={currency}
                  handleAddToCart={handleAddToCart}
                  handleRemoveFromCart={handleRemoveFromCart}
                  parentProduct={parentProduct}
                  currentApp={currentApp}
                  onClose={handleClose}
                />
              </>
            </ScrollableView>
            {selectedProduct && (
              <ScrollableView
                childClassName={'Main-Content-Detail'}
                header={
                  <DetailHeader
                    back={settings.text.back}
                    resetSelectedProduct={resetSelectedProduct}
                  />
                }
                footer={
                  <Footer
                    hasRejectButton={
                      isCandyRackSettings(settings) &&
                      settings.is_reject_enabled
                    }
                    text={settings.text}
                    handleReject={handleReject}
                    handleContinue={handleContinue}
                    rejectInProgress={rejectInProgress}
                    isLoading={isLoading}
                    selectedProduct={selectedProduct}
                  />
                }
                onClose={handleCloseDetail}
              >
                {productDetail}
              </ScrollableView>
            )}
          </Modal>
          {error && onError && (
            <ErrorMessage onClick={onError}>
              {error.message || error.errorCode}
            </ErrorMessage>
          )}
        </ModalContainer>
      </Iframe>
    </PopupContextProvider>
  );
};

export default Popup;
